/**
 * DriveUploadProgress — iter189f live ticker.
 * Renders a small sticky bottom-right card that polls the job every 2s
 * and shows "N / total uploaded" plus a slim progress bar. Auto-hides
 * ~4s after the job finishes so it doesn't linger.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Cloud, CloudOff, X, CheckCircle2 } from "lucide-react";

export default function DriveUploadProgress({ jobId, onClose }) {
  const [job, setJob] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    let stopped = false;
    let hideTimer = null;
    const poll = async () => {
      try {
        const r = await api.get(`/admin/files/drive-jobs/${jobId}`);
        if (stopped) return;
        setJob(r.data);
        if (r.data?.status === "done" || r.data?.status === "aborted") {
          const total = r.data.total || 0;
          const ok = r.data.uploaded || 0;
          const fail = r.data.failed || 0;
          if (r.data.status === "aborted" && r.data.abort_reason === "drive_reauth_needed") {
            toast.error("Drive OAuth expired — reconnect Google Drive in Settings, then try again");
          } else if (fail > 0) toast.warning(`Drive upload done · ${ok}/${total} uploaded · ${fail} failed`);
          else if (total > 0) toast.success(`Drive upload complete · ${ok}/${total} files pushed`);
          hideTimer = setTimeout(() => { if (!stopped) onClose?.(); }, r.data.status === "aborted" ? 12000 : 4000);
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!stopped) setTimeout(poll, 3000);
      }
    };
    poll();
    return () => { stopped = true; if (hideTimer) clearTimeout(hideTimer); };
  }, [jobId, onClose]);

  if (!jobId || !job) return null;

  const total = job.total || 0;
  const uploaded = job.uploaded || 0;
  const failed = job.failed || 0;
  const processed = uploaded + failed;
  const pct = total ? Math.floor((processed / total) * 100) : 0;
  const done = job.status === "done";
  const aborted = job.status === "aborted";
  const reauth = aborted && job.abort_reason === "drive_reauth_needed";

  return (
    <div className="fixed bottom-6 right-6 z-[999] w-[340px] rounded-xl border border-border bg-white shadow-lg p-4"
         data-testid="drive-upload-progress">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {reauth ? (
            <CloudOff size={16} className="text-[#EF4444]" />
          ) : done ? (
            <CheckCircle2 size={16} className="text-[#10B981]" />
          ) : failed > 0 ? (
            <CloudOff size={16} className="text-[#F59E0B]" />
          ) : (
            <Cloud size={16} className="text-teal animate-pulse" />
          )}
          <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            {reauth ? "Drive disconnected" : done ? "Drive upload done" : "Uploading to Drive"}
          </span>
        </div>
        <button onClick={onClose} data-testid="drive-upload-progress-close"
                className="text-muted-foreground hover:text-foreground">
          <X size={14} />
        </button>
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-2xl font-light tracking-tighter font-[Outfit]" data-testid="drive-upload-progress-count">
          {processed}
        </span>
        <span className="text-sm text-muted-foreground">/ {total} files</span>
        {failed > 0 && (
          <span className="ml-auto text-xs text-[#EF4444]" data-testid="drive-upload-progress-failed">
            {failed} failed
          </span>
        )}
      </div>
      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
        <div className={`h-full transition-all duration-500 ${done ? "bg-[#10B981]" : "bg-teal"}`}
             style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[11px] text-muted-foreground mt-2">
        {reauth ? (
          <span className="text-[#EF4444]">
            OAuth token expired — <a href="/settings" className="underline font-semibold" data-testid="drive-upload-reauth-link">reconnect Google Drive</a> and click Upload again.
          </span>
        ) : done ? (
          `Completed at ${(job.finished_at || "").slice(11, 19)}`
        ) : (
          `${pct}% — this runs in the background, you can navigate away`
        )}
      </div>
    </div>
  );
}
